import {
  CurrencyCode,
  enumCurrencyCode,
  enumPaymentMethod,
  enumPaymentType,
} from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import { paymentCreateAtom } from '../_graphql/mutationPaymentCreate';
import { paymentGetNextDocumentNumberAtom } from '../_graphql/mutationPaymentGetNextDocumentNumber';
import { nextCheckNumberQueryAtom } from '../_graphql/queryNextCheckNumber';
import { PaymentFormState } from '../types';

import { now } from '~/shared/utils/dateTime';

const initialPaymentFormState: PaymentFormState = {
  id: undefined,
  paymentType: enumPaymentType.Bill,
  paymentMethod: enumPaymentMethod.ACH,
  amount: null,
  transactionDate: null,
  accountId: null,
  financialAccountId: null,
  checkFacilityId: null,
  checkNumber: null,
  checkMemo: null,
  achNumber: null,
  wireNumber: null,
};

export const $paymentForm = atom<PaymentFormState>(initialPaymentFormState);
export const $isSubmitting = atom<boolean>(false);
export const $errors = atom<Record<string, string>>({});
export const $isPaymentWizardOpen = atom<boolean>(false);
export const $currencyCode = atom<CurrencyCode>(enumCurrencyCode.USD);

export const $updatePaymentForm = atom(
  null,
  (get, set, updates: Partial<PaymentFormState>) => {
    const currentForm = get($paymentForm);
    set($paymentForm, { ...currentForm, ...updates });
  },
);

export const $resetPaymentForm = atom(null, (get, set) => {
  set($paymentForm, {
    ...initialPaymentFormState,
    transactionDate: now(),
  });
  set($errors, {});
  set($currencyCode, enumCurrencyCode.USD);
});

export const PAYMENT_REQUIRED_FIELDS = [
  'paymentType',
  'paymentMethod',
  'amount',
  'transactionDate',
] as const;

export type PaymentRequiredField = (typeof PAYMENT_REQUIRED_FIELDS)[number];

const fieldValidators: Record<
  PaymentRequiredField,
  (form: PaymentFormState) => boolean
> = {
  paymentType: (form) => !form.paymentType,
  paymentMethod: (form) => !form.paymentMethod,
  amount: (form) => !form.amount || form.amount.amount <= 0,
  transactionDate: (form) => !form.transactionDate,
};

const fieldErrorMessages: Record<PaymentRequiredField, string> = {
  paymentType: 'Payment type is required',
  paymentMethod: 'Payment method is required',
  amount: 'Amount is required and must be greater than 0',
  transactionDate: 'Transaction date is required',
};

const getMissingRequiredFields = (
  form: PaymentFormState,
): PaymentRequiredField[] =>
  PAYMENT_REQUIRED_FIELDS.filter((field) => fieldValidators[field](form));

export const $validatePaymentForm = atom(
  null,
  (get, set): { isValid: boolean; errors: Record<string, string> } => {
    const form = get($paymentForm);
    const missingFields = getMissingRequiredFields(form);

    const errors = missingFields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: fieldErrorMessages[field],
      }),
      {} as Record<string, string>,
    );

    set($errors, errors);
    return { isValid: Object.keys(errors).length === 0, errors };
  },
);

export const $paymentMissingRequiredFields = atom((get) => {
  const form = get($paymentForm);
  return getMissingRequiredFields(form);
});

export const $paymentIsMissingRequired = atom(
  (get) => get($paymentMissingRequiredFields).length > 0,
);

// 🟠🟠🟠 ACTION - Next Document Number
export const $nextPaymentDocumentNumber = paymentGetNextDocumentNumberAtom();

export const $nextCheckNumber = nextCheckNumberQueryAtom((get) => {
  const form = get($paymentForm);
  return {
    variables: { financialAccountId: form.financialAccountId ?? '' },
    skip: form.paymentMethod !== 'Check' || !form.financialAccountId,
  };
});

export const $createPayment = paymentCreateAtom();
